<template>
  <div id="SectionLackTransitionPlans">
    <UIContainer class="SectionLackTransitionPlans__container">
      <div class="SectionLackTransitionPlans__title-container">
        <SectionTitle titleSize="sm">
          <template #title>
            71% of companies with net zero commitments lack credible transition
            plans
          </template>
        </SectionTitle>
      </div>

      <div class="SectionLackTransitionPlans__cards">
        <div
          class="SectionLackTransitionPlans__card SectionLackTransitionPlans__card--negative"
        >
          <div class="SectionLackTransitionPlans__card-icon">
            <UIIcon name="closeCircle" />
          </div>
          <h4 class="SectionLackTransitionPlans__card-title">
            The yearly fire drill
          </h4>
          <p class="SectionLackTransitionPlans__card-description">
            Scattered utility bills, missing sub-meters, and ad-hoc refrigerant
            top-ups make inventories brittle. Add material EPD variability and
            carrier files, and you’ve got a yearly fire drill. We replace that
            with a governed pipeline and evidence you can defend.
          </p>
        </div>
        <img
          class="SectionLackTransitionPlans__image-arrow"
          src="../assets/lackTransitionPlans/arrow.avif"
          loading="lazy"
        />
        <div
          class="SectionLackTransitionPlans__card SectionLackTransitionPlans__card--positive"
        >
          <div class="SectionLackTransitionPlans__card-icon">
            <UIIcon name="checkCircle" />
          </div>
          <h4 class="SectionLackTransitionPlans__card-title">
            The governed data pipeline
          </h4>
          <p class="SectionLackTransitionPlans__card-description">
            We replace that chaos with a governed pipeline and evidence you can
            defend in any audit. Real-time data flows, automated calculations,
            and documentation that stands up to the toughest scrutiny.
          </p>
        </div>
      </div>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
</script>

<style lang="scss" scoped>
#SectionLackTransitionPlans {
  position: relative;
  background-color: $color-white;
  .SectionLackTransitionPlans__image-container {
    display: flex;
  }

  .SectionLackTransitionPlans__title-container {
    display: flex;
    justify-content: center;
    padding-bottom: 3rem;
  }

  .SectionTitle {
    max-width: 46rem;
    text-align: center;
  }

  .SectionLackTransitionPlans__container {
    text-align: initial;
  }

  .SectionLackTransitionPlans__cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    align-items: center;

    @include md {
      grid-template-columns: 1fr auto 1fr;
      gap: 3rem;
    }
  }

  .SectionLackTransitionPlans__card {
    background-color: $color-ui-gray-cool-50;
    padding: 1.5rem;
    text-align: left;
    height: 100%;

    @include md {
      padding: 2rem;
    }
  }

  .SectionLackTransitionPlans__card-icon {
    --size: 1.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
    margin-bottom: 2rem;
  }

  .SectionLackTransitionPlans__card-title {
    @include font-size-18px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin-bottom: 0.5rem;
  }

  .SectionLackTransitionPlans__card-description {
    @include font-size-16px();
    color: $color-ui-gray-cool-700;
  }

  .SectionLackTransitionPlans__card--negative {
    .SectionLackTransitionPlans__card-icon {
      background-color: $color-ui-error-600;
      --color: #fff;
    }
  }

  .SectionLackTransitionPlans__card--positive {
    .SectionLackTransitionPlans__card-icon {
      background-color: $color-ui-success-main;
      --color: #fff;
    }
  }

  .SectionLackTransitionPlans__image {
    display: none;
    background-image: url('../assets/manufacturingPlatform/bg.avif');
    height: 100%;
    background-size: auto 100%;
    background-position: left center;
    background-repeat: no-repeat;
    margin-right: -14rem;

    @include md {
      display: block;
      right: 0;
      margin-left: 1rem;
    }
  }

  .SectionLackTransitionPlans__image-arrow {
    width: 17.5rem;
    opacity: 0.6;
  }
  .SectionLackTransitionPlans__title {
    @include font-size-20px();
    font-weight: 600;
    line-height: 1.2;
    color: $color-ui-gray-cool-900;
    margin-bottom: 1rem;

    @include md {
      @include font-size-24px();
    }
  }

  .SectionLackTransitionPlans__features {
    display: grid;
    gap: 1.25rem;
    padding-left: 1rem;
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  .SectionLackTransitionPlans__feature {
    @include font-size-18px();
    display: flex;
    align-items: center;
    color: $color-ui-gray-cool-600;
    gap: 0.75rem;
  }

  .SectionLackTransitionPlans__feature-icon {
    --size: 1.75rem;
  }

  .SectionLackTransitionPlans__cta {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
}
</style>
